import { X } from 'lucide-react'
import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { mergeClassNames } from '@/design-system/lib/mergeClassNames'

/** Ligne du résumé mobile. `block` ouvre un nouveau bloc (adresse, prochaine action). */
export interface ListMobileCardLine {
  key: string
  content: ReactNode
  tone?: 'default' | 'muted' | 'danger' | 'warning'
  /** `strong` : ligne de niveau 2 (nom du client sous un numéro de contrat). */
  emphasis?: 'strong'
  block?: boolean
}

/**
 * Résumé compact d'une ligne sur téléphone : identité et statut, puis deux à quatre lignes utiles.
 * Pas de libellé de section, pas d'identifiant interne : la fiche complète porte le reste.
 */
export interface ListMobileCard {
  /** Texte de l'identité (nom, numéro de contrat) : nom accessible du bouton qui ouvre la fiche. */
  title: string
  status?: ReactNode
  lines: readonly (ListMobileCardLine | false | null | undefined)[]
}

/**
 * Action secondaire de l'appui long. Une action asynchrone (copier) affiche `feedback` une fois
 * réussie, `failure` sinon : jamais « Copié » quand le presse-papiers a refusé.
 */
export interface ListMobileAction {
  key: string
  label: string
  onSelect: () => void | Promise<void>
  feedback?: string
  failure?: string
}

/** Appui long : 500 ms, comme les menus contextuels natifs; 10 px de tolérance avant d'y voir un défilement. */
export const LONG_PRESS_MS = 500
const MOVE_TOLERANCE_PX = 10
const NOTICE_MS = 2500
const SUPPRESS_AFTER_PRESS_MS = 800
/**
 * La feuille s'ouvre sous le doigt encore posé : son relâchement (puis le clic que le navigateur en
 * tire) ne doit choisir aucune option. Elle n'accepte un choix que 350 ms après le relâchement.
 */
const RELEASE_GRACE_MS = 350
const RELEASE_EVENTS = ['pointerup', 'pointercancel', 'touchend', 'touchcancel'] as const
const HINT_STORAGE_KEY = 'reca.lists.longPressHint.v1'

function readHintSeen(): boolean {
  try {
    return window.localStorage.getItem(HINT_STORAGE_KEY) === 'seen'
  } catch {
    // Stockage bloqué (navigation privée) : l'aide reste affichée, sans conséquence.
    return false
  }
}

function writeHintSeen() {
  try {
    window.localStorage.setItem(HINT_STORAGE_KEY, 'seen')
  } catch {
    // Idem : l'aide réapparaîtra au prochain chargement.
  }
}

interface MobileCardProps {
  card: ListMobileCard
  openLabel: string
  className?: string | undefined
  leading?: ReactNode
  onOpen: () => void
  /** `held` : le doigt (ou le bouton) est encore posé quand la feuille s'ouvre. */
  onLongPress: (returnFocusTo: HTMLElement | null, held: boolean) => void
}

/**
 * Carte entière = ouvrir la fiche (bouton du titre étiré sur toute la carte). Appui long ou menu
 * contextuel (clic droit, Maj+F10, touche Menu) = actions secondaires. Un défilement annule l'appui :
 * le navigateur envoie `pointercancel` dès qu'il prend le geste, et un déplacement de plus de 10 px
 * l'annule aussi. Aucun balayage horizontal : il entrerait en conflit avec le défilement.
 */
function MobileCard({ card, openLabel, className, leading, onOpen, onLongPress }: MobileCardProps) {
  const openButton = useRef<HTMLButtonElement>(null)
  const timer = useRef<number | undefined>(undefined)
  const origin = useRef<{ x: number; y: number } | null>(null)
  // Moment du dernier appui long : le clic qui le suit (souris, certains navigateurs tactiles) ne doit
  // pas ouvrir la fiche, et le `contextmenu` qu'Android envoie au même moment ne rouvre rien.
  const lastTrigger = useRef(0)
  const [holding, setHolding] = useState(false)

  const cancel = () => {
    window.clearTimeout(timer.current)
    timer.current = undefined
    origin.current = null
    setHolding(false)
  }

  useEffect(
    () => () => {
      window.clearTimeout(timer.current)
    },
    [],
  )

  const trigger = () => {
    const held = origin.current !== null
    cancel()
    lastTrigger.current = Date.now()
    // Retour haptique léger là où il existe (Android); sans effet ailleurs.
    if ('vibrate' in navigator) navigator.vibrate(10)
    onLongPress(openButton.current, held)
  }

  const recentlyTriggered = () => Date.now() - lastTrigger.current < SUPPRESS_AFTER_PRESS_MS

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return
    origin.current = { x: event.clientX, y: event.clientY }
    setHolding(true)
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(trigger, LONG_PRESS_MS)
  }

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const start = origin.current
    if (!start) return
    if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > MOVE_TOLERANCE_PX) cancel()
  }

  const onContextMenu = (event: ReactMouseEvent<HTMLDivElement>) => {
    // Remplace le menu natif (Android, clic droit) seulement sur la carte; l'appui long y mène déjà.
    event.preventDefault()
    if (!recentlyTriggered()) trigger()
  }

  const onClickCapture = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!recentlyTriggered()) return
    event.preventDefault()
    event.stopPropagation()
  }

  const onKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    // Maj+F10 déclenche déjà `contextmenu` dans les navigateurs, mais pas partout (jsdom).
    if (event.key === 'F10' && event.shiftKey) {
      event.preventDefault()
      if (!recentlyTriggered()) trigger()
    }
  }

  const lines = card.lines.filter((line): line is ListMobileCardLine => Boolean(line))

  return (
    <div
      role="listitem"
      className={mergeClassNames('ds-list-mcard', holding && 'ds-list-mcard--holding', className)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={cancel}
      onPointerCancel={cancel}
      onPointerLeave={cancel}
      onContextMenu={onContextMenu}
      onClickCapture={onClickCapture}
    >
      <div className="ds-list-mcard__top">
        {leading}
        <button
          ref={openButton}
          type="button"
          className="ds-list-mcard__open"
          aria-keyshortcuts="Shift+F10"
          title={openLabel}
          onKeyDown={onKeyDown}
          // Un contrôle de la carte (téléphone, case) est au-dessus du bouton étiré : son clic n'arrive pas ici.
          onClick={onOpen}
        >
          {card.title}
        </button>
        {card.status ? <span className="ds-list-mcard__status">{card.status}</span> : null}
      </div>
      {lines.map((line) => (
        <div
          key={line.key}
          className={mergeClassNames(
            'ds-list-mcard__line',
            line.tone && line.tone !== 'default' && `ds-list-mcard__line--${line.tone}`,
            line.emphasis === 'strong' && 'ds-list-mcard__line--strong',
            line.block && 'ds-list-mcard__line--block',
          )}
        >
          {line.content}
        </div>
      ))}
    </div>
  )
}

interface ActionSheetProps {
  title: string
  items: readonly ListMobileAction[]
  onSelect: (action: ListMobileAction) => void
  onClose: () => void
  /** Faux tant que le doigt de l'appui long n'est pas levé (plus le délai de grâce). */
  isArmed: () => boolean
}

/** Feuille d'actions au bas de l'écran, au-dessus de la navigation basse et de la zone sûre. */
function ActionSheet({ title, items, onSelect, onClose, isArmed }: ActionSheetProps) {
  const panel = useRef<HTMLDivElement>(null)

  useEffect(() => {
    panel.current?.querySelector<HTMLButtonElement>('button')?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
        return
      }
      if (event.key !== 'Tab' || !panel.current) return
      // Focus gardé dans la feuille tant qu'elle est ouverte.
      const buttons = [...panel.current.querySelectorAll<HTMLButtonElement>('button')]
      const first = buttons[0]
      const last = buttons[buttons.length - 1]
      if (!first || !last) return
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  return createPortal(
    <div className="ds-list-sheet">
      <div
        className="ds-list-sheet__backdrop"
        aria-hidden="true"
        onClick={() => {
          if (isArmed()) onClose()
        }}
      />
      <div ref={panel} className="ds-list-sheet__panel" role="dialog" aria-modal="true" aria-label={title}>
        <p className="ds-list-sheet__title">{title}</p>
        <div className="ds-list-sheet__items">
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              className="ds-list-sheet__item"
              onClick={() => {
                if (isArmed()) onSelect(item)
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="ds-list-sheet__item ds-list-sheet__cancel"
          onClick={() => {
            if (isArmed()) onClose()
          }}
        >
          Annuler
        </button>
      </div>
    </div>,
    document.body,
  )
}

export interface ListMobileCardsProps<TItem> {
  label: string
  items: readonly TItem[]
  rowKey: (item: TItem) => string
  rowName: (item: TItem) => string
  card: (item: TItem) => ListMobileCard
  openLabel: string
  onOpen: (item: TItem) => void
  /** Actions de l'appui long, après « Ouvrir ». */
  actions: (item: TItem) => readonly ListMobileAction[]
  rowClassName?: ((item: TItem) => string | undefined) | undefined
  leading?: ((item: TItem, name: string) => ReactNode) | undefined
}

/**
 * Liste mobile compacte commune (Clients, Demandes, Contrats) : tap carte → ouvrir, appui long →
 * actions secondaires, tap téléphone → appeler, défilement → naviguer. Aucune action essentielle
 * n'est seulement derrière l'appui long : elles restent dans la fiche.
 */
export function ListMobileCards<TItem>({
  label,
  items,
  rowKey,
  rowName,
  card,
  openLabel,
  onOpen,
  actions,
  rowClassName,
  leading,
}: ListMobileCardsProps<TItem>) {
  const [menu, setMenu] = useState<{ item: TItem; returnFocusTo: HTMLElement | null } | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [hintSeen, setHintSeen] = useState(readHintSeen)
  const noticeTimer = useRef<number | undefined>(undefined)
  // Moment à partir duquel la feuille accepte un choix; l'infini tant que le doigt est posé.
  const armedAt = useRef(0)

  useEffect(
    () => () => {
      window.clearTimeout(noticeTimer.current)
    },
    [],
  )

  const showNotice = (text: string) => {
    window.clearTimeout(noticeTimer.current)
    setNotice(text)
    noticeTimer.current = window.setTimeout(() => {
      setNotice(null)
    }, NOTICE_MS)
  }

  const dismissHint = () => {
    writeHintSeen()
    setHintSeen(true)
  }

  /** Écoute posée dès l'ouverture, avant le rendu de la feuille : aucun relâchement n'échappe. */
  const holdUntilRelease = () => {
    armedAt.current = Number.POSITIVE_INFINITY
    const release = () => {
      armedAt.current = Date.now() + RELEASE_GRACE_MS
      for (const type of RELEASE_EVENTS) document.removeEventListener(type, release, true)
    }
    for (const type of RELEASE_EVENTS) document.addEventListener(type, release, true)
  }

  const closeMenu = () => {
    menu?.returnFocusTo?.focus()
    setMenu(null)
  }

  const run = (action: ListMobileAction) => {
    closeMenu()
    const failed = () => {
      showNotice(action.failure ?? 'Action impossible pour le moment.')
    }
    // Appel synchrone : Safari n'autorise presse-papiers et nouvel onglet que dans le geste lui-même.
    let result: void | Promise<void>
    try {
      result = action.onSelect()
    } catch {
      failed()
      return
    }
    void Promise.resolve(result).then(() => {
      if (action.feedback) showNotice(action.feedback)
    }, failed)
  }

  const menuActions = (item: TItem): ListMobileAction[] => [
    {
      key: '__open',
      label: openLabel,
      onSelect: () => {
        onOpen(item)
      },
    },
    ...actions(item),
  ]

  return (
    <>
      {hintSeen ? null : (
        <p className="ds-list-hint">
          <span>Maintenez une carte pour plus d’actions.</span>
          <button
            type="button"
            className="ds-list-hint__close"
            aria-label="Masquer l’aide"
            onClick={dismissHint}
          >
            <X aria-hidden="true" size={16} />
          </button>
        </p>
      )}
      <div className="ds-list-cards ds-list-cards--compact" role="list" aria-label={label}>
        {items.map((item) => {
          const name = rowName(item)
          return (
            <MobileCard
              key={rowKey(item)}
              card={card(item)}
              openLabel={`${openLabel} ${name}`}
              className={rowClassName?.(item)}
              leading={leading?.(item, name)}
              onOpen={() => {
                onOpen(item)
              }}
              onLongPress={(returnFocusTo, held) => {
                if (!hintSeen) dismissHint()
                if (held) holdUntilRelease()
                else armedAt.current = 0
                setMenu({ item, returnFocusTo })
              }}
            />
          )
        })}
      </div>
      {menu ? (
        <ActionSheet
          title={card(menu.item).title}
          items={menuActions(menu.item)}
          onSelect={run}
          onClose={closeMenu}
          isArmed={() => Date.now() >= armedAt.current}
        />
      ) : null}
      <p className={mergeClassNames('ds-list-notice', notice && 'ds-list-notice--visible')} role="status">
        {notice}
      </p>
    </>
  )
}
